import { useEffect, useRef, useState } from "react";
import { apiConnectorPost } from "../../utils/APIConnector";
import { endpoint } from "../../utils/APIRoutes";
import { useNavigate } from "react-router-dom";
import logo1 from "../../assets/logo.png";

const staticMessages = [];

const DoubleCheck = ({ status }) => {
  const color = status === "read" ? "#2563EB" : "#475569";
  return (
    <svg width="16" height="11" viewBox="0 0 16 11" fill="none" style={{ marginLeft: 3 }}>
      <path d="M1 5.5L4.5 9L10 3" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 5.5L9.5 9L15 3" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const SingleCheck = () => (
  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" style={{ marginLeft: 3 }}>
    <path d="M1 5.5L4.5 9L11 2" stroke="#475569" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export default function TicketAndSupport() {
  const [messages, setMessages] = useState(staticMessages);
  const [input, setInput] = useState("");
  const bottomRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    setMessages((prev) => [...prev, { id: Date.now(), text: input.trim(), sent: false, time, status: null }]);
    await apiConnectorPost(endpoint?.user_message, { message: input.trim() });
    setInput("");
  };

  const getUserMessages = async () => {
    try {
      setLoading(true);
      const response = await apiConnectorPost(endpoint?.get_user_message);
      const rawMessages = response?.data?.result?.data || [];

      const formattedMessages = rawMessages.reduce((acc, item) => {
        const userText = item?.tr54_user_msg?.toString()?.trim();
        const replyText = item?.tr54_reply?.toString()?.trim();
        const userTime = formatTime(item?.tr54_msg_date);
        const replyTime = formatTime(item?.tr54_reply_date || item?.tr54_msg_date);
        const isSingleTick = Number(item?.tr54_status) === 0;

        if (userText) {
          acc.push({
            id: `${item?.tr54_msg_date || Date.now()}-user-${acc.length}`,
            text: userText,
            sent: false,
            time: userTime,
            status: null,
          });
        }
        if (replyText) {
          acc.push({
            id: `${item?.tr54_reply_date || Date.now()}-reply-${acc.length}`,
            text: replyText,
            sent: true,
            time: replyTime,
            status: isSingleTick ? "sent" : "delivered",
          });
        }
        return acc;
      }, []);

      setMessages(formattedMessages.length ? formattedMessages : staticMessages);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserMessages();
  }, []);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const skeletonRows = [
    { id: 1, sent: false, width: "42%" },
    { id: 2, sent: true, width: "34%" },
    { id: 3, sent: false, width: "50%" },
    { id: 4, sent: true, width: "28%" },
    { id: 5, sent: false, width: "38%" },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        background: "linear-gradient(160deg, #060E1C 0%, #080F1C 50%, #0B1E36 100%)",
        fontFamily: "'SF Pro Display', -apple-system, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Grid overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 1,
          backgroundImage:
            "linear-gradient(rgba(37,99,235,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          pointerEvents: "none",
        }}
      />
      {/* Ambient glow */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 600,
          height: 250,
          borderRadius: "50%",
          background: "radial-gradient(ellipse, rgba(37,99,235,0.10) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 16px",
          background: "rgba(7,21,38,0.95)",
          borderBottom: "1px solid rgba(30,58,95,0.5)",
          boxShadow: "0 2px 20px rgba(0,0,0,0.4)",
          zIndex: 10,
          flexShrink: 0,
          backdropFilter: "blur(12px)",
          position: "relative",
        }}
      >
        {/* Top accent */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: "linear-gradient(90deg, transparent, rgba(37,99,235,0.6), transparent)",
          }}
        />
        {/* Left accent bar */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 2,
            background: "linear-gradient(180deg, #22D3EE, #2563EB, #1D4ED8)",
          }}
        />

        {/* Back button */}
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            background: "rgba(37,99,235,0.08)",
            border: "1px solid rgba(37,99,235,0.2)",
            borderRadius: 10,
            cursor: "pointer",
            padding: "6px 8px",
            color: "#7DD3FC",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(37,99,235,0.18)";
            e.currentTarget.style.borderColor = "rgba(37,99,235,0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(37,99,235,0.08)";
            e.currentTarget.style.borderColor = "rgba(37,99,235,0.2)";
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>

        {/* Avatar */}
        <div style={{ position: "relative" }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 14px rgba(37,99,235,0.4)",
            }}
          >
            <img src={logo1} alt="support" style={{ width: 24, height: 24, objectFit: "contain" }} />
          </div>
          <div
            style={{
              position: "absolute",
              bottom: 1,
              right: 1,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#22D3EE",
              border: "2px solid rgba(7,21,38,0.95)",
            }}
          />
        </div>

        {/* Name + status */}
        <div style={{ flex: 1 }}>
          <div style={{ color: "#F1F5F9", fontWeight: 700, fontSize: 14, letterSpacing: "0.3px" }}>
            HEXAFUND Support
          </div>
          <div style={{ color: "#22D3EE", fontSize: 11, fontWeight: 500 }}>● online</div>
        </div>

        {/* Action icons */}
        <div style={{ display: "flex", gap: 8 }}>
          {[
            "M15 10l-4 4m0-4l4 4M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
            "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 7V5z",
          ].map((d, i) => (
            <button
              key={i}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#334155",
                padding: "6px",
                borderRadius: 8,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#7DD3FC";
                e.currentTarget.style.background = "rgba(37,99,235,0.10)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#334155";
                e.currentTarget.style.background = "none";
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d={d} />
              </svg>
            </button>
          ))}
        </div>
      </div>

      {/* ── Messages ── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(37,99,235,0.3) transparent",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Date stamp */}
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <span
            style={{
              background: "rgba(30,58,95,0.5)",
              color: "#475569",
              fontSize: 10,
              padding: "4px 14px",
              borderRadius: 20,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              border: "1px solid rgba(30,58,95,0.6)",
            }}
          >
            Today
          </span>
        </div>

        {loading ? (
          skeletonRows.map((row) => (
            <div
              key={row.id}
              style={{
                display: "flex",
                justifyContent: row.sent ? "flex-end" : "flex-start",
                marginTop: 8,
                paddingLeft: row.sent ? 48 : 0,
                paddingRight: row.sent ? 0 : 48,
              }}
            >
              <div
                style={{
                  width: row.width,
                  maxWidth: "100%",
                  height: 44,
                  borderRadius: 16,
                  border: "1px solid rgba(30,58,95,0.4)",
                  background: "linear-gradient(110deg, rgba(7,21,38,0.8) 25%, rgba(37,99,235,0.06) 40%, rgba(7,21,38,0.8) 60%)",
                  backgroundSize: "200% 100%",
                  animation: "chatSkeletonPulse 1.15s ease-in-out infinite",
                }}
              />
            </div>
          ))
        ) : messages.length === 0 ? (
          /* Empty state */
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              paddingTop: 60,
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 20,
                background: "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(29,78,216,0.10))",
                border: "1px solid rgba(37,99,235,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 30px rgba(37,99,235,0.15)",
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7DD3FC" strokeWidth="1.5" strokeLinecap="round">
                <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ color: "#94A3B8", fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No messages yet</p>
              <p style={{ color: "#334155", fontSize: 12 }}>Send a message to start the conversation</p>
            </div>
          </div>
        ) : (
          messages.map((msg, i) => {
            const prevSent = i > 0 && messages[i - 1].sent === msg.sent;
            return (
              <div
                key={msg.id}
                style={{
                  display: "flex",
                  justifyContent: msg.sent ? "flex-end" : "flex-start",
                  marginTop: prevSent ? 2 : 10,
                  paddingLeft: msg.sent ? 56 : 0,
                  paddingRight: msg.sent ? 0 : 56,
                }}
              >
                <div
                  style={{
                    maxWidth: "100%",
                    background: msg.sent
                      ? "linear-gradient(135deg, rgba(37,99,235,0.18) 0%, rgba(29,78,216,0.14) 100%)"
                      : "rgba(11,30,54,0.7)",
                    borderRadius: msg.sent
                      ? prevSent ? "16px 4px 4px 16px" : "16px 4px 16px 16px"
                      : prevSent ? "4px 16px 16px 4px" : "4px 16px 16px 16px",
                    padding: "9px 13px 7px",
                    border: msg.sent
                      ? "1px solid rgba(37,99,235,0.35)"
                      : "1px solid rgba(30,58,95,0.5)",
                    boxShadow: msg.sent
                      ? "0 2px 12px rgba(37,99,235,0.15)"
                      : "0 2px 8px rgba(0,0,0,0.25)",
                  }}
                >
                  <div style={{ color: msg.sent ? "#E2E8F0" : "#CBD5E1", fontSize: 14, lineHeight: 1.55, wordBreak: "break-word" }}>
                    {msg.text}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, marginTop: 3 }}>
                    <span style={{ color: "#334155", fontSize: 10, fontWeight: 500 }}>{msg.time}</span>
                    {msg.sent && (
                      msg.status === "sent" ? <SingleCheck /> : <DoubleCheck status={msg.status} />
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <style>{`
        @keyframes chatSkeletonPulse {
          0% { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
        textarea::placeholder { color: #334155; }
      `}</style>

      {/* ── Input Bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 8,
          padding: "10px 12px",
          background: "rgba(7,21,38,0.97)",
          borderTop: "1px solid rgba(30,58,95,0.5)",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.3)",
          flexShrink: 0,
          backdropFilter: "blur(12px)",
          position: "relative",
          zIndex: 10,
        }}
      >
        {/* Emoji button */}
        <button
          style={{ background: "none", border: "none", cursor: "pointer", color: "#334155", padding: "8px 6px", flexShrink: 0, transition: "color 0.2s" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#7DD3FC")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#334155")}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" />
          </svg>
        </button>

        {/* Text area wrapper */}
        <div
          style={{
            flex: 1,
            background: "rgba(11,30,54,0.8)",
            borderRadius: 22,
            border: "1px solid rgba(30,58,95,0.6)",
            display: "flex",
            alignItems: "center",
            padding: "0 4px 0 14px",
            transition: "border-color 0.2s, box-shadow 0.2s",
          }}
          onFocusCapture={(e) => {
            e.currentTarget.style.borderColor = "rgba(37,99,235,0.6)";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(37,99,235,0.10)";
          }}
          onBlurCapture={(e) => {
            e.currentTarget.style.borderColor = "rgba(30,58,95,0.6)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Type a message..."
            rows={1}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              outline: "none",
              color: "#E2E8F0",
              fontSize: 14,
              resize: "none",
              fontFamily: "inherit",
              lineHeight: 1.5,
              padding: "9px 0",
              maxHeight: 100,
              overflowY: "auto",
              scrollbarWidth: "none",
            }}
          />
          {/* Attachment button */}
          <button
            style={{ background: "none", border: "none", cursor: "pointer", color: "#334155", padding: "8px 6px", flexShrink: 0, transition: "color 0.2s" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#7DD3FC")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#334155")}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
        </div>

        {/* Send button */}
        <button
          onClick={sendMessage}
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            background: input.trim()
              ? "linear-gradient(135deg, #2563EB, #1D4ED8)"
              : "rgba(11,30,54,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "all 0.2s ease",
            transform: input.trim() ? "scale(1)" : "scale(0.92)",
            boxShadow: input.trim() ? "0 4px 18px rgba(37,99,235,0.4)" : "none",
            border: input.trim() ? "none" : "1px solid rgba(30,58,95,0.4)",
          }}
          onMouseEnter={(e) => { if (input.trim()) e.currentTarget.style.transform = "scale(1.08)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = input.trim() ? "scale(1)" : "scale(0.92)"; }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={input.trim() ? "#ffffff" : "#334155"}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
}
